import { useState, useRef, useEffect } from 'preact/hooks';

interface PlanChipProps {
  title: string;
  project: string;
  command: string;
  onRunHere: (command: string) => void;
  onShare: (command: string) => void;
}

/**
 * Renders the active plan as a clickable pill in the chat header.
 * Title is extracted from the H1 of {project}/plans/current-plan.md by the backend.
 * Tap → dropdown: "Run here" (prefills input) or "Run in new session" (triggers share+auto-submit flow).
 */
export function PlanChip({ title, project, command, onRunHere, onShare }: PlanChipProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div class="plan-chip-wrap" ref={wrapRef}>
      <button
        type="button"
        class="plan-chip"
        onClick={() => setOpen((v) => !v)}
        title={`Active plan: ${title} (${project})`}
      >
        <span class="plan-chip-icon">📋</span>
        <span class="plan-chip-title">{title}</span>
        <span class="plan-chip-caret">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div class="plan-chip-menu">
          <button
            type="button"
            class="plan-chip-menu-item"
            onClick={() => { onRunHere(command); setOpen(false); }}
          >
            ▶ Run here
          </button>
          <button
            type="button"
            class="plan-chip-menu-item"
            onClick={() => { onShare(command); setOpen(false); }}
          >
            ↗ Run in new session
          </button>
        </div>
      )}
    </div>
  );
}
